import {
  getNestedOrFloatingElementById,
  convertToSLSRectangle,
  createFieldValue,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'

/**
 * Creates dispatches for reassigning highlights from linked fields to a target field
 * @param {Object} params - The parameters object
 * @param {Object} params.highlightsContext - The highlights context with getHighlights method
 * @param {Array} params.linkedFields - Array of linked field objects
 * @param {Object} params.activeFieldInfo - The active field info with id
 * @param {Array} params.images - Array of images from state
 * @param {string} params.fieldValue - The field value to assign
 * @param {Function} params.handleSave - Callback to execute the dispatches
 * @param {string} params.appName - The app name for attribution
 * @returns {void}
 */
export const createReassignHighlightDispatches = ({
  highlightsContext,
  linkedFields,
  activeFieldInfo,
  images,
  fieldValue,
  handleSave,
  appName,
}) => {
  if (!appName) throw new Error('createReassignHighlightDispatches: appName is required parameter')

  // Get the highlights being reassigned from the SlsHighlightsContext
  const { mainEditHighlights, splitScreenEditHighlights } = highlightsContext.getHighlights()

  // Find highlights that belong to the linked fields (the ones we're stealing from)
  const linkedFieldIds = linkedFields?.map((field) => field.id) || []
  const highlightsToSteal = [
    ...mainEditHighlights.filter((highlight) => linkedFieldIds.includes(highlight.originalHighlight.fieldId)),
    ...splitScreenEditHighlights.filter((highlight) => linkedFieldIds.includes(highlight.originalHighlight.fieldId)),
  ]

  const dispatches = []

  // 1. ADD_FV on the field being edited to put in the new transcription
  const targetFieldId = activeFieldInfo?.id
  const targetField = getNestedOrFloatingElementById(images, targetFieldId)

  if (targetField && fieldValue) {
    const attribution = {
      comment: '',
      reasonId: INDEXED_INCORRECTLY,
      app: appName,
    }

    const fieldValueObj = createFieldValue(fieldValue, attribution)

    dispatches.push({
      type: 'ADD_FV',
      value: {
        id: targetFieldId,
        partitionKey: targetField.partitionKey,
        fieldValue: fieldValueObj,
      },
    })
  }

  // 2. For each highlight: REM_RECT to remove from current field, then ADD_RECT to add to target field
  highlightsToSteal.forEach((highlight) => {
    // Remove highlight from its current field
    dispatches.push({
      type: 'REM_RECT',
      value: {
        id: highlight.originalHighlight.fieldId,
        partitionKey: linkedFields.find((field) => field.id === highlight.originalHighlight.fieldId).partitionKey,
        rect: convertToSLSRectangle(highlight),
      },
    })
    // Add highlight to the target field
    dispatches.push({
      type: 'ADD_RECT',
      value: {
        id: targetFieldId,
        partitionKey: targetField?.partitionKey,
        rect: convertToSLSRectangle(highlight),
      },
    })
  })

  handleSave(dispatches)
}

// Helper function to get linked fields
export const getLinkedFields = (
  getHighlights,
  slsImages,
  activeFieldInfo,
  mainEditHighlights = null,
  splitScreenEditHighlights = null
) => {
  const highlightHookResult = getHighlights()

  // Combine passed highlights with those from the hook if not provided
  const editHighlights = [
    ...(mainEditHighlights?.length ? mainEditHighlights : highlightHookResult.mainEditHighlights),
    ...(splitScreenEditHighlights?.length ? splitScreenEditHighlights : highlightHookResult.splitScreenEditHighlights),
  ]
  const selectedHighlights = editHighlights.filter(
    (highlight) => highlight.attached && highlight?.fieldId === activeFieldInfo?.id
  )

  return selectedHighlights
    .map((highlight) => getNestedOrFloatingElementById(slsImages, highlight?.originalHighlight?.fieldId))
    .filter(Boolean)
    .filter((field) => field?.id !== activeFieldInfo?.id)
}
